import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Scale, ShieldCheck, Landmark } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className={`block overflow-hidden pb-1 ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const STATS = [
  { icon: Scale, label: "Admitted to the Bar", value: "31.01.2017" },
  { icon: Landmark, label: "Practice Areas", value: "09" },
  { icon: ShieldCheck, label: "Indemnity Cover", value: "KShs 10M" },
];

export default function Hero({ onNavigate }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img
          src="https://images.unsplash.com/photo-1589578527966-fdac0f44566c?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Lady Justice in gold light"
          className="w-full h-[120%] object-cover opacity-[0.12] animate-kenburns"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-950/70 via-navy-950/60 to-navy-950" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-950 via-transparent to-navy-950/60" />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1, delay: 1.6, ease: EASE }}
        className="hidden lg:flex absolute right-16 top-36 z-0 items-center justify-center"
        data-testid="hero-seal-badge"
        aria-hidden="true"
      >
        <div className="relative h-44 w-44">
          <svg viewBox="0 0 100 100" className="animate-spin-slow h-full w-full">
            <defs>
              <path id="seal-circle" d="M50,50 m-40,0 a40,40 0 1,1 80,0 a40,40 0 1,1 -80,0" />
            </defs>
            <text
              className="fill-gold"
              style={{ fontSize: "6.2px", letterSpacing: "2.2px", fontFamily: "'JetBrains Mono', monospace" }}
            >
              <textPath href="#seal-circle">
                MUSESYA &amp; COMPANY ADVOCATES &#8226; HIGH COURT OF KENYA &#8226; EST. 2018 &#8226;
              </textPath>
            </text>
          </svg>
          <span className="absolute inset-0 m-auto h-16 w-16 rounded-full border border-gold/30 flex items-center justify-center bg-navy-950/60 backdrop-blur-sm">
            <Scale size={24} className="text-gold" />
          </span>
        </div>
      </motion.div>

      <motion.div style={{ opacity: fade }} className="max-w-7xl mx-auto px-6 lg:px-10 w-full pt-32 pb-20">
        <motion.img
          src="/assets/logo.jpg"
          alt="Musesya & Company Advocates"
          data-testid="hero-logo-large"
          initial={{ opacity: 0, y: 40, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.2, ease: EASE }}
          className="w-60 sm:w-80 lg:w-96 mb-10"
          style={{
            maskImage:
              "radial-gradient(ellipse 70% 70% at 46% 50%, black 42%, transparent 86%)",
            WebkitMaskImage:
              "radial-gradient(ellipse 70% 70% at 46% 50%, black 42%, transparent 86%)",
          }}
        />
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9, delay: 0.4, ease: EASE }}
          className="inline-flex items-center gap-3 border border-gold/30 rounded-full px-4 py-1.5 mb-10 bg-navy-900/50 backdrop-blur-sm"
          data-testid="hero-bar-badge"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-gold" />
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold">
            Advocate of the High Court of Kenya
          </span>
        </motion.div>

        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.14] tracking-tight text-ink max-w-4xl">
          <MaskedLine delay={0.55}>Uncompromising Legal Integrity.</MaskedLine>
          <MaskedLine delay={0.7} className="text-ink-dim">
            Precision Advocacy in Kenya.
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
          className="mt-6 font-mono text-xs sm:text-sm text-gold tracking-[0.12em]"
          data-testid="hero-credentials"
        >
          Samson K. Musesya &middot; LL.B (Hons) UoN &middot; Dip. K.S.L. &middot; MTC (Moi University &ndash; KESRA)
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
          className="mt-8 max-w-xl text-base sm:text-lg text-ink-dim leading-relaxed"
          data-testid="hero-subtext"
        >
          Musesya &amp; Company Advocates provides tax counsel, litigation,
          conveyancing and commercial advisory from Muthaiga Square, Nairobi —
          responsive, accountable, and governed by The Advocates Act.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.3, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-contact-button"
            onClick={() => onNavigate("contact")}
            className="bg-gold text-navy-950 font-medium px-8 py-4 rounded-full hover:bg-gold-hover transition-colors duration-300 shadow-[0_0_35px_rgba(154,136,15,0.3)]"
          >
            Request a Consultation
          </button>
          <button
            data-testid="hero-practice-button"
            onClick={() => onNavigate("practice")}
            className="border border-ink/20 text-ink px-8 py-4 rounded-full hover:border-gold/60 hover:text-gold transition-colors duration-300"
          >
            Explore Practice Areas
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.55 }}
          className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl"
          data-testid="hero-stats"
        >
          {STATS.map((s) => (
            <div
              key={s.label}
              className="flex items-center gap-3 border border-gold/15 rounded-2xl px-5 py-4 bg-navy-900/40 backdrop-blur-sm"
            >
              <s.icon size={18} className="text-gold shrink-0" />
              <div>
                <div className="font-mono text-sm text-ink">{s.value}</div>
                <div className="text-xs text-ink-muted">{s.label}</div>
              </div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-cue"
        onClick={() => onNavigate("about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-ink-muted hover:text-gold transition-colors"
        aria-label="Scroll to about section"
      >
        <ArrowDown size={18} />
      </motion.button>
    </section>
  );
}
